/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useCallback } from 'react'
import { Checkbox, Dropdown, Icon, Loader, Menu, MenuItem, Tab, TabPane } from 'semantic-ui-react'
import CategoryEdit from './CategoryEdit'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import {
    getCategories,
    toggleCategoryActivation,
    updateCategoryPosition,
} from '../../services/Category/Category.service'
import useUser from '../Shared/UserProvider/useUser'
import { UltyModalWrapperContext } from '../Shared/UltyModalWrapper/UltyModalWrapperContext'
import CategoryModal from './CategoryModal'
import CategoryModifiedModal from './CategoryModifiedModal'
import useModified from './useModified'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isProviderAPos } from '../../services/Provider/Provider.service'
import { useParams } from 'react-router-dom-v5-compat'

const menuItemStyles = css`
    color: teal;

    &:hover {
        color: grey;
`

const CategoryMenuItemContent = ({
                                     slot,
                                     category,
                                     position,
                                     maxPosition,
                                     isLoading,
                                     onCategoryPositionChange,
                                     isCategoryOwnedByCurrentProvider,
                                 }) => {
    const {user, canExtendCategories} = useUser()
    const handleCategoryPositionChange = async (e, category, position) => {
        e.stopPropagation()
        onCategoryPositionChange(category, position)
    }

    const queryClient = useQueryClient()
    const toggleCategoryActivationMutation = useMutation({
        mutationFn: ({category}) => toggleCategoryActivation(category),
        onMutate: async ({ category }) => {
            await queryClient.cancelQueries({queryKey: ['sets', category.slot.categorySetId, 'slots', category.slot.id, 'categories']})
            const previousCategories = queryClient.getQueryData(['sets', category.slot.categorySetId, 'slots', category.slot.id, 'categories'])

            queryClient.setQueryData(['sets', category.slot.categorySetId, 'slots', category.slot.id, 'categories'], (prev) => {
                if (!prev) return prev
                return prev.map(c => c.id === category.id ? {
                    ...c,
                    overrides: c.overrides.map(override => {
                        if (override.providerId === user.provider.id) {
                            const isCurrentlyActive = override.activatedAt && !override.deactivatedAt
                            return {
                                ...override,
                                activatedAt: isCurrentlyActive ? override.activatedAt : new Date().toISOString(),
                                deactivatedAt: isCurrentlyActive ? new Date().toISOString() : null,
                            }
                        }
                        return override
                    })
                } : c)
            })

            return { previousCategories }
        },
        onError: (err, { category }, context) => {
            if (context?.previousCategories) {
                queryClient.setQueryData(['sets', category.slot.categorySetId, 'slots', category.slot.id, 'categories'], context.previousCategories)
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({queryKey: ['sets', category.slot.categorySetId, 'slots', category.slot.id, 'categories']})
        }
    })

    const slotOverride = slot?.overrides?.find(override => override.providerId === user.provider.id)
    const isSlotActive = slotOverride ?
      (slotOverride.activatedAt !== null && slotOverride.deactivatedAt === null)
      : true

    const overrideForCurrentProvider = category.overrides.find(override => override.providerId === user.provider.id)
    const isCategoryActive = isSlotActive && (overrideForCurrentProvider ?
      (overrideForCurrentProvider.activatedAt !== null && overrideForCurrentProvider.deactivatedAt === null) :
      true)

    return (
      <div css={css`
          display: flex;
          align-items: center;
      `}>
          <span css={css`
              flex: 1;
              opacity: ${isCategoryActive ? 1 : 0.5};
          `}>
              {category.name}
          </span>

          <div css={css`min-height: 21px;`}>
              {(canExtendCategories() && !isCategoryOwnedByCurrentProvider) && (
                <Checkbox
                  toggle
                  checked={isCategoryActive}
                  onChange={() => {
                      toggleCategoryActivationMutation.mutate({category})
                  }}
                />
              )}

              {isLoading && (
                <Loader active size="tiny" inline="centered" />
              )}

              {isCategoryOwnedByCurrentProvider && !isLoading && (
                <Dropdown
                  scrolling
                  options={[...Array(maxPosition).keys()].map(p => ({
                      key: `${category.id}-position-${p}`,
                      text: `${p + 1}`,
                      value: p + 1
                  }))}
                  value={position}
                  onChange={async (evt, {value}) => {
                      await handleCategoryPositionChange(evt, category, value)
                  }}
                />
              )}

          </div>
      </div>
    )
}

CategoryMenuItemContent.propTypes = {
    category: PropTypes.object.isRequired,
    position: PropTypes.number.isRequired,
    maxPosition: PropTypes.number.isRequired,
    isLoading: PropTypes.bool,
    onCategoryPositionChange: PropTypes.func.isRequired,
}

const CategoryList = ({ slot }) => {
    const {user, canCreateCategories, canExtendCategories} = useUser()
    const [t] = useTranslation()
    const [canAdd, setCanAdd] = useState(true)
    const [activeIndex, setActiveIndex] = useState(0)
    const {modified, setModified} = useModified()
    const {id: setId, slotId} = useParams()
    const queryClient = useQueryClient()

    const { data: categories, isLoading } = useQuery({
        queryKey: ['sets', setId, 'slots', slotId, 'categories'],
        queryFn: () => getCategories(setId, slotId)
    })

    const {handleUltyModalWrapper} = React.useContext(UltyModalWrapperContext)
    const defineImportModal = () => {
        const modalSettings = {
            title: t('category_list.import_selection'),
            component: <CategoryModal
              currentSet={setId}
              currentSlot={slot.id}
              afterSubmit={async () => {
                  await queryClient.invalidateQueries({queryKey: ['sets', setId, 'slots', slot.id, 'categories']})
                  handleUltyModalWrapper(false, null)
              }}
            />,
        }
        handleUltyModalWrapper(true, modalSettings)
    }

    const defineConfirmModal = useCallback((callback) => {
        const modalSettings = {
            title: t('categories.confirm_exit'),
            component: <CategoryModifiedModal
              afterSubmit={(result) => {
                  handleUltyModalWrapper(false, null)

                  if (result) {
                      setModified(false)
                      callback()
                  }
              }}
            />,
        }
        handleUltyModalWrapper(true, modalSettings)
    }, [handleUltyModalWrapper, setModified, t])

    const handleSaveCategory = async () => {
        await queryClient.invalidateQueries({queryKey: ['sets', setId, 'slots', slot.id, 'categories']})
        setCanAdd(true)
    }

    const handleSaveCategoryCallback = useCallback(handleSaveCategory, [queryClient, setId, slot.id])

    const handleDeleteCategory = async () => {
        setActiveIndex(activeIndex === 0 ? 0 : activeIndex - 1)
        await queryClient.invalidateQueries({queryKey: ['sets', setId, 'slots', slot.id, 'categories']})
        setCanAdd(true)
    }

    const handleDeleteCategoryCallback = useCallback(handleDeleteCategory, [activeIndex, queryClient, setId, slot.id])

    const handleCategoryPositionChange = async (category, position) => {
        const callback = async () => {
            await updateCategoryPosition(setId, slot.id, {
                ...category,
                position,
            })
            await queryClient.invalidateQueries({queryKey: ['sets', setId, 'slots', slot.id, 'categories']})
        }

        if (modified) {
            defineConfirmModal(callback)
        } else {
            callback()
        }
    }

    const handleCategoryPositionChangeCallback = useCallback(handleCategoryPositionChange, [modified, setId, slot.id, queryClient, defineConfirmModal])

    if (!categories) {
        return null
    }

    const panes = categories.map(c => ({
        menuItem: (
          <MenuItem
            key={c.id}
            css={css`
                border-left: 3px solid ${isProviderAPos(user.provider) && c.providerId === user.provider.id ? '#21ba45' : '#2185d0'} !important;
            `}
          >
              <CategoryMenuItemContent
                slot={slot}
                category={c}
                position={c.position}
                maxPosition={categories.length}
                isLoading={isLoading}
                onCategoryPositionChange={handleCategoryPositionChangeCallback}
                isCategoryOwnedByCurrentProvider={c.providerId === user.provider.id}
              />
          </MenuItem>
        ),
        render: () => {
            const slotOverride = slot?.overrides?.find(override => override.providerId === user.provider.id)
            const isSlotActive = slotOverride ?
              (slotOverride.activatedAt !== null && slotOverride.deactivatedAt === null)
              : true

            const overrideForCurrentProvider = c.overrides.find(override => override.providerId === user.provider.id)
            const isCategoryActive = isSlotActive && (overrideForCurrentProvider ?
              (overrideForCurrentProvider.activatedAt !== null && overrideForCurrentProvider.deactivatedAt === null) :
              true)

            return (
              <TabPane>
                  <CategoryEdit
                    setId={setId}
                    slotId={slot.id}
                    category={c}
                    onSaveCategory={handleSaveCategoryCallback}
                    onDeleteCategory={handleDeleteCategoryCallback}
                    isParent={c.slot.providerId !== user.provider.id}
                  />
                  {!isCategoryActive && (
                    <div css={css`
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background-color: rgba(255, 255, 255, 0.5);
                        backdrop-filter: blur(.6px);
                        z-index: 910;
                    `} />
                  )}
              </TabPane>
            )
        },
    }))

    if (canExtendCategories() || canCreateCategories()) {
        if (canAdd) {
            panes.push({
                menuItem: (
                  <Menu.Item key={'add-category-tab'}>
                      <div css={css`
                              display: flex;
                              align-items: center;
                          `}>
                          <span css={css`flex: 1;`}>{t('category_list.new_category')}</span>

                          <div css={css`min-height: 21px;`}>
                              {isLoading
                                ? <Loader active size="tiny" inline="centered" />
                                : <Icon name={'plus square'} size={'large'} css={menuItemStyles}/>}
                          </div>
                      </div>
                  </Menu.Item>
                ),
                render: () => null,
            })
        } else {
            panes.push({
                menuItem: t('category_list.new_category'),
                render: () => <Tab.Pane>
                    <CategoryEdit
                      setId={setId}
                      slotId={slot.id}
                      onSaveCategory={handleSaveCategoryCallback}
                      onDeleteCategory={handleDeleteCategoryCallback}
                      isParent={false}
                    />
                </Tab.Pane>,
            })
        }

        panes.push({
            menuItem: (
              <Menu.Item key={'import-category-tab'} onClick={defineImportModal}>
                  <div css={css`
                          display: flex;
                          align-items: center;
                      `}>
                      <span css={css`flex: 1;`}>{t('category_list.import_category')}</span>

                      <div css={css`min-height: 21px;`}>
                          {isLoading
                            ? <Loader active size="tiny" inline="centered" />
                            : <Icon name={'upload'} size={'large'} css={menuItemStyles}/>}
                      </div>
                  </div>
              </Menu.Item>
            ),
            render: () => null,
        })
    }

    const handleCategoryTabChange = (e, data) => {
        const callback = () => {
            const itemKey = data.panes[data.activeIndex].menuItem?.key
            if (itemKey === 'import-category-tab') {
                return
            }

            setActiveIndex(data.activeIndex)

            if (itemKey === 'add-category-tab') {
                setCanAdd(false)
            }
        }

        if (modified) {
            defineConfirmModal(callback)
        } else {
            callback()
        }
    }

    return (
      <Tab
        activeIndex={activeIndex}
        panes={panes}
        onTabChange={handleCategoryTabChange}
        menu={{fluid: true, vertical: true, tabular: true}}
        grid={{paneWidth: 11, tabWidth: 5}}
      />
    )
}

export default CategoryList
